import React from "react";
import { Form, Format, useFormField } from "@redriver/cinnamon";
import { useSelector } from "react-redux";
import AddReturnOnInvestment from "./AddReturnOnInvestment";
import EditReturnOnInvestment from "./EditReturnOnInvestment";
import { Icon } from "semantic-ui-react";
import FormArrayDeleteModal from "features/../../../shared/components/forms/FormArrayDeleteModal";
import { getCalculationData, getRoiLabel } from "features/Sheets";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { ImpersonatedUserPermissionCheck } from "modules/ImpersonatedUserPermissions";
import { useTranslation } from "react-i18next";

const getCalculatedValue = (calculatedReturnOnInvestments = [], index) =>
  calculatedReturnOnInvestments[index]?.calculatedValue ?? null;

const getCalculatedValueForReturnOnInvestment = (
  calculatedReturnOnInvestments = [],
  returnOnInvestmentId,
  index,
) => {
  const calculatedReturnOnInvestment = calculatedReturnOnInvestments.find(
    (x) => x.returnOnInvestmentId === returnOnInvestmentId,
  );

  return calculatedReturnOnInvestment
    ? calculatedReturnOnInvestment.calculatedValue
    : getCalculatedValue(calculatedReturnOnInvestments, index);
};

const ListReturnOnInvestments = ({
  dealerOfficeId,
  disabled,
  isCustomerView,
}) => {
  const [returnOnInvestments, setReturnOnInvestments] = useFormField(
    "returnOnInvestments",
  );
  const calculatedReturnOnInvestments =
    useSelector(getCalculationData)?.breakdown?.systemSchedule
      ?.returnOnInvestments ?? [];

  const roiLabel = useSelector(getRoiLabel);

  const { t } = useTranslation("sheetTranslation", {
    keyPrefix: "ReturnOnInvestment",
  });

  const updateReturnOnInvestmentCustomerView = (id, showInCustomerView) => {
    setReturnOnInvestments(
      (returnOnInvestments || []).map((returnOnInvestment) =>
        `${returnOnInvestment.id}` === `${id}`
          ? {
              ...returnOnInvestment,
              showInCustomerView,
            }
          : returnOnInvestment,
      ),
    );
  };

  return (
    <ImpersonatedUserPermissionCheck
      target={Targets.SheetReturnOnInvestment}
      action={Actions.View}
    >
      <AddReturnOnInvestment
        dealerOfficeId={dealerOfficeId}
        disabled={disabled}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: 10,
          width: "fit-content",
          height: "max-content",
        }}
      >
        <h5 style={{ fontWeight: 500 }}>
          {roiLabel || t("ReturnOnInvestmentLabel")}
        </h5>
        <Form.Array
          field="returnOnInvestments"
          arrayKey="id"
          propagateUpdates="always"
          disabled={disabled}
        >
          {({ fields, arrayIndex }) =>
            (!isCustomerView || fields.showInCustomerView) && (
              <ReturnOnInvestmentItem
                key={fields.id}
                id={fields.id}
                name={fields.name}
                showInCustomerView={fields.showInCustomerView}
                value={getCalculatedValueForReturnOnInvestment(
                  calculatedReturnOnInvestments,
                  fields.returnOnInvestmentId,
                  arrayIndex,
                )}
                disabled={disabled}
                onUpdateCustomerView={updateReturnOnInvestmentCustomerView}
              />
            )
          }
        </Form.Array>
      </div>
    </ImpersonatedUserPermissionCheck>
  );
};

const ReturnOnInvestmentItem = ({
  id,
  name,
  showInCustomerView,
  value = null,
  disabled,
  onUpdateCustomerView,
}) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        minHeight: "2rem",
        padding: "0.25rem 0",
        width: "100%",
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
        }}
      >
        per {name}
        <span style={{ marginLeft: "0.5rem", fontWeight: 500 }}>
          {value == null ? (
            `${process.env.MAJOR_CURRENCY}NIL`
          ) : (
            <Format.Number
              value={value}
              thousandSeparator
              decimalPlaces={2}
              prefix={process.env.MAJOR_CURRENCY}
            />
          )}
        </span>
      </span>

      <span
        style={{
          display: "inline-flex",
          justifyContent: "center",
          minWidth: "1rem",
        }}
      >
        {showInCustomerView && (
          <Icon
            name="check"
            color="green"
            title="Shown in customer view"
            style={{ margin: 0 }}
          />
        )}
      </span>

      <EditReturnOnInvestment
        id={id}
        name={name}
        showInCustomerView={showInCustomerView}
        disabled={disabled}
        onUpdateCustomerView={onUpdateCustomerView}
      />
      <ImpersonatedUserPermissionCheck
        target={Targets.SheetReturnOnInvestment}
        action={Actions.Delete}
      >
        <FormArrayDeleteModal nameOfRemoval={name} disabled={disabled} />
      </ImpersonatedUserPermissionCheck>
    </div>
  );
};

export default ListReturnOnInvestments;
